import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { isBrandedPreset } from '../../theme';
import { useHrPendingNotifications } from '../../hooks/useHrPendingNotifications';
import EventTable from './DashboardEventTable';

const buildNotificationsPalette = (theme) => {
  const tokens = theme.fw?.tokens;
  if (isBrandedPreset(theme.fw?.preset) && tokens) {
    const lightNav = Boolean(theme.fw?.lightNav);
    const brandAccent = tokens.accent || tokens.black || tokens.ink;
    return {
      accent: lightNav ? brandAccent : tokens.accent,
      headerBg: lightNav ? brandAccent : tokens.warning,
      headerText: tokens.white,
      showHeaderAccent: !lightNav,
      hoverBg: lightNav ? tokens.subtle : alpha(tokens.warning, 0.12),
      rowAltBg: lightNav ? tokens.subtle : alpha(tokens.warning, 0.06),
    };
  }
  return {
    accent: '#ed6c02',
    headerBg: '#ed6c02',
    headerText: '#FFFFFF',
    hoverBg: 'rgba(237, 108, 2, 0.14)',
    rowAltBg: 'rgba(237, 108, 2, 0.06)',
  };
};

const DashboardNotificationsWidget = ({ headerDragClassName, loading: dashboardLoading }) => {
  const theme = useTheme();
  const palette = useMemo(() => buildNotificationsPalette(theme), [theme]);
  const navigate = useNavigate();
  const {
    manager,
    pendingReceiptCount,
    pendingTimeOffCount,
    unreadMessageCount,
    total
  } = useHrPendingNotifications();

  const items = useMemo(() => {
    if (!manager) return [];
    return [
      {
        id: 'receipts',
        label: 'Expense receipts',
        count: pendingReceiptCount,
        path: '/hr/expense-reimbursements'
      },
      {
        id: 'time-off',
        label: 'Time off requests',
        count: pendingTimeOffCount,
        path: '/hr/time-off-requests'
      },
      {
        id: 'messages',
        label: 'Employee messages',
        count: unreadMessageCount,
        path: '/hr/employee-messages'
      }
    ];
  }, [manager, pendingReceiptCount, pendingTimeOffCount, unreadMessageCount]);

  const title = total > 0 ? `Notifications (${total})` : 'Notifications';

  return (
    <EventTable
      title={title}
      items={items}
      emptyText={manager ? 'No pending notifications' : '—'}
      loading={dashboardLoading}
      showColumnHeaders
      tableLayout="auto"
      headerDragClassName={headerDragClassName}
      onRowClick={(row) => navigate(row.path)}
      palette={palette}
      columns={[
        {
          key: 'type',
          header: 'Type',
          minWidth: 140,
          render: (row) => row.label
        },
        {
          key: 'count',
          header: 'Pending',
          width: '1%',
          minWidth: 72,
          headerAlign: 'right',
          noTruncate: true,
          render: (row) => (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Chip
                label={row.count > 99 ? '99+' : row.count}
                size="small"
                color={row.count > 0 ? 'warning' : 'default'}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  minWidth: 28,
                  ...(row.count > 0 ? {} : { color: 'text.secondary' })
                }}
              />
            </Box>
          )
        }
      ]}
    />
  );
};

export default DashboardNotificationsWidget;
